import { useState } from 'react';
import { Box, SimpleGrid, Stack, Group, Badge, Avatar, TextInput, Select } from '@mantine/core';
import { IconSearch, IconPlus, IconMail, IconPhone, IconBuilding } from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockContacts, Contact } from '../mockData/crm';

export default function ContactsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const statuses = ['lead', 'prospect', 'customer', 'churned'];

  const filteredContacts = mockContacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(search.toLowerCase()) ||
                          contact.email.toLowerCase().includes(search.toLowerCase()) ||
                          contact.company.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || contact.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: Contact['status']) => {
    switch (status) {
      case 'customer': return 'green';
      case 'prospect': return 'blue';
      case 'lead': return 'cyan';
      case 'churned': return 'red';
      default: return 'gray';
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Contacts</Title>
          <Text c="dimmed" size="sm">{filteredContacts.length} contacts</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Add Contact</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search contacts..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Contacts Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {filteredContacts.map(contact => (
          <Card key={contact.id}>
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Avatar size="lg" radius="xl" color="blue">
                    {contact.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Box>
                    <Text fw={600}>{contact.name}</Text>
                    <Text size="sm" c="dimmed">{contact.title}</Text>
                  </Box>
                </Group>
                <Badge color={getStatusColor(contact.status)}>{contact.status}</Badge>
              </Group>

              <Stack gap={4}>
                <Group gap="xs">
                  <IconBuilding size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm">{contact.company}</Text>
                </Group>
                <Group gap="xs">
                  <IconMail size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm">{contact.email}</Text>
                </Group>
                <Group gap="xs">
                  <IconPhone size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm">{contact.phone}</Text>
                </Group>
              </Stack>

              <Group justify="space-between">
                <Group gap="xs">
                  {contact.tags.slice(0, 2).map(tag => (
                    <Badge key={tag} size="xs" variant="light">{tag}</Badge>
                  ))}
                </Group>
                <Box ta="right">
                  <Text size="xs" c="dimmed">Score</Text>
                  <Text size="sm" fw={600} c={contact.score >= 70 ? 'green' : contact.score >= 40 ? 'yellow' : 'red'}>
                    {contact.score}
                  </Text>
                </Box>
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
